// Stahlus – modernes Frontend: Lenis Smooth-Scroll + Swup (Seitenwechsel ohne Reload)
// + native View Transitions + Scroll-Effekte (Parallax, gestaffelte Reveals, Fortschrittsbalken)
import Lenis from 'lenis';
import Swup from 'swup';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const supportsVT = typeof document !== 'undefined' && typeof document.startViewTransition === 'function';

/* ──────────────────────────────────────────────
   1. Lenis Smooth Scrolling
   ────────────────────────────────────────────── */
let lenis = null;
let parallaxEls = [];
let zoomEls = [];
const progressBar = document.getElementById('scroll-progress');
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

function updateScrollEffects(scroll = window.scrollY) {
  // Fortschrittsbalken
  if (progressBar) {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? Math.min(scroll / max, 1) : 0;
    progressBar.style.transform = `scaleX(${p})`;
  }
  // Header-Zustand
  const header = document.getElementById('site-header');
  if (header) header.classList.toggle('is-scrolled', scroll > 40);
  // Parallax
  for (const el of parallaxEls) {
    const speed = parseFloat(el.dataset.parallax) || 0.15;
    const rect = el.parentElement.getBoundingClientRect();
    const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
    el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
  }
  // Scroll-Zoom: Bild zoomt sanft heran, während die Sektion durch den Viewport läuft
  const vh = window.innerHeight || document.documentElement.clientHeight;
  for (const el of zoomEls) {
    const min = parseFloat(el.dataset.scrollZoomMin) || 1.05;
    const max = parseFloat(el.dataset.scrollZoomMax) || 1.18;
    const rect = el.parentElement.getBoundingClientRect();
    const p = clamp((vh - rect.top) / (vh + rect.height), 0, 1);
    el.style.transform = `scale(${(min + (max - min) * p).toFixed(4)})`;
  }
}

if (!reduceMotion) {
  lenis = new Lenis({ duration: 1.1, smoothWheel: true, lerp: 0.1 });
  lenis.on('scroll', (e) => updateScrollEffects(e.scroll));
  const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
  requestAnimationFrame(raf);
} else {
  window.addEventListener('scroll', () => updateScrollEffects(), { passive: true });
}

/* ──────────────────────────────────────────────
   2. Scroll-Reveal (Intersection Observer, gestaffelt)
   ────────────────────────────────────────────── */
let revealObserver = null;
function setupReveals(root = document) {
  const els = root.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
  }
  // gestaffelte Verzögerung für Geschwister in derselben Gruppe
  const groups = new Map();
  els.forEach((el) => {
    if (el.dataset.revealReady) return;
    el.dataset.revealReady = '1';
    if (!el.style.transitionDelay) {
      const parent = el.parentElement;
      const i = groups.get(parent) || 0;
      groups.set(parent, i + 1);
      if (i > 0) el.style.transitionDelay = `${Math.min(i * 80, 480)}ms`;
    }
    revealObserver.observe(el);
  });
}

/* ──────────────────────────────────────────────
   3. Parallax-Elemente einsammeln
   ────────────────────────────────────────────── */
function setupParallax(root = document) {
  if (reduceMotion) {
    // Reduced Motion: kein Scroll-Zoom, aber Bild leicht gefüllt (statisch) lassen
    document.querySelectorAll('[data-scroll-zoom]').forEach((el) => {
      el.style.transform = `scale(${parseFloat(el.dataset.scrollZoomMin) || 1.05})`;
    });
    return;
  }
  parallaxEls = Array.from(document.querySelectorAll('[data-parallax]'));
  zoomEls = Array.from(document.querySelectorAll('[data-scroll-zoom]'));
  updateScrollEffects();
}

/* ──────────────────────────────────────────────
   4. Galerie-Lightbox
   ────────────────────────────────────────────── */
function setupLightbox(root = document) {
  const lightbox = document.getElementById('lightbox');
  const lightboxImg = document.getElementById('lightbox-img');
  const lightboxClose = document.getElementById('lightbox-close');
  if (!lightbox || !lightboxImg) return;

  const open = (src) => {
    lightboxImg.src = src;
    lightbox.classList.remove('hidden');
    lightbox.classList.add('flex');
    lenis && lenis.stop();
    document.body.style.overflow = 'hidden';
  };
  const close = () => {
    lightbox.classList.add('hidden');
    lightbox.classList.remove('flex');
    lightboxImg.src = '';
    lenis && lenis.start();
    document.body.style.overflow = '';
  };

  // Trigger pro Seite (neu) binden
  root.querySelectorAll('[data-lightbox]').forEach((btn) => {
    if (btn.dataset.lbBound) return;
    btn.dataset.lbBound = '1';
    btn.addEventListener('click', () => open(btn.getAttribute('data-lightbox')));
  });

  // Globale Handler nur einmal binden (Lightbox liegt persistent im Layout)
  if (!lightbox.dataset.bound) {
    lightbox.dataset.bound = '1';
    lightboxClose && lightboxClose.addEventListener('click', close);
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !lightbox.classList.contains('hidden')) close();
    });
  }
}

/* ──────────────────────────────────────────────
   5a. Konfigurator (Built-to-Order)
   ────────────────────────────────────────────── */
function setupConfigurator(root = document) {
  const wrap = root.querySelector('[data-configurator]');
  if (!wrap) return;

  const swatches = Array.from(wrap.querySelectorAll('.swatch'));
  const tint = document.querySelector('[data-tint]');
  const configInput = document.querySelector('[data-config-input]');
  const form = document.querySelector('[data-config-form]');

  const selectedFor = (categoryId) =>
    wrap.querySelector('.swatch.is-selected[data-category="' + categoryId + '"]');

  function updateSummaries() {
    document.querySelectorAll('[data-summary-cat]').forEach((row) => {
      const sel = selectedFor(row.dataset.summaryCat);
      if (!sel) return;
      const nameEl = row.querySelector('[data-summary-name]');
      const chipEl = row.querySelector('[data-summary-chip]');
      if (nameEl) nameEl.textContent = sel.dataset.name;
      if (chipEl) chipEl.style.backgroundColor = sel.dataset.hex || '#888';
    });
  }

  function updateTint() {
    if (!tint) return;
    const ext = wrap.querySelector('.swatch.is-selected[data-category-key="exterior"]');
    if (ext) tint.style.backgroundColor = ext.dataset.hex;
  }

  function buildConfig() {
    const cfg = {};
    wrap.querySelectorAll('.swatch.is-selected').forEach((s) => {
      cfg[s.dataset.category] = parseInt(s.dataset.option, 10);
    });
    if (configInput) configInput.value = JSON.stringify(cfg);
  }

  function select(swatch) {
    const cat = swatch.dataset.category;
    wrap.querySelectorAll('.swatch[data-category="' + cat + '"]').forEach((s) => {
      const on = s === swatch;
      s.classList.toggle('is-selected', on);
      s.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    updateTint();
    updateSummaries();
    buildConfig();
  }

  swatches.forEach((s) => {
    s.setAttribute('tabindex', '0');
    s.addEventListener('click', () => select(s));
    s.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(s); }
    });
  });

  if (form) form.addEventListener('submit', buildConfig);

  // Initialzustand (Defaults kommen serverseitig markiert)
  updateTint();
  updateSummaries();
  buildConfig();
}

/* ──────────────────────────────────────────────
   5. Pro-Seite-Initialisierung (auch nach Swup-Wechsel)
   ────────────────────────────────────────────── */
function initPage(root = document) {
  setupReveals(root);
  setupParallax(root);
  setupLightbox(root);
  setupConfigurator(root);
  updateScrollEffects(0);
}

/* ──────────────────────────────────────────────
   6. Mobile-Menü (Header bleibt über Swup bestehen → einmal binden)
   ────────────────────────────────────────────── */
const menuToggle = document.getElementById('menu-toggle');
const mobileMenu = document.getElementById('mobile-menu');
if (menuToggle && mobileMenu) {
  menuToggle.addEventListener('click', () => mobileMenu.classList.toggle('hidden'));
}
function closeMobileMenu() { mobileMenu && mobileMenu.classList.add('hidden'); }

/* Sprach-Links auf aktuelle Seite zeigen lassen + aktive Nav markieren (Header liegt außerhalb #swup) */
function updateChrome() {
  const segs = location.pathname.replace(/^\/+|\/+$/g, '').split('/');
  const lang = segs[0] || 'de';
  const rest = segs.slice(1).join('/');
  document.querySelectorAll('.js-lang-link').forEach((a) => {
    const l = a.dataset.lang;
    a.href = '/' + l + (rest ? '/' + rest : '');
    a.classList.toggle('text-brass', l === lang);
    a.classList.toggle('font-medium', l === lang);
    a.classList.toggle('text-fog', l !== lang);
  });
  document.querySelectorAll('.js-nav-link').forEach((a) => {
    const base = (a.dataset.nav || '').split('/')[0];
    const active = base && (rest === base || rest.startsWith(base));
    a.classList.toggle('text-brass', !!active);
    a.classList.toggle('text-bone/80', !active);
  });
}

/* ──────────────────────────────────────────────
   7. Swup – Seitenwechsel ohne Reload (+ native View Transitions)
   ────────────────────────────────────────────── */
const swup = new Swup({
  containers: ['#swup'],
  native: supportsVT,                                   // nutzt View Transitions API wo verfügbar
  animationSelector: supportsVT ? false : '[class*="transition-"]',
  linkSelector: 'a[href]:not([data-no-swup]):not([target="_blank"]):not([download])',
});

// Shared-Element-Morph: geklicktes Fahrzeugbild bekommt denselben Namen wie das Detail-Hero
if (supportsVT) {
  swup.hooks.on('visit:start', (visit) => {
    try {
      const el = visit?.trigger?.el;
      const img = el && el.querySelector('[data-vt="vehicle"]');
      if (img) img.style.viewTransitionName = 'vehicle-hero';
    } catch (_) { /* Morph ist nur Bonus – niemals den Wechsel blockieren */ }
  });
}

// Nach jedem Seitenwechsel: neue Inhalte initialisieren, nach oben scrollen, Menü schließen
swup.hooks.on('page:view', () => {
  closeMobileMenu();
  updateChrome();
  if (lenis) lenis.scrollTo(0, { immediate: true });
  else window.scrollTo(0, 0);
  initPage(document);
});

// Sicherheitsnetz: falls eine Animation hängt, Inhalte trotzdem sichtbar machen
swup.hooks.on('visit:end', () => {
  document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
    .forEach((el) => { if (!el.dataset.revealReady) el.classList.add('is-visible'); });
});

/* ── Erststart ── */
initPage(document);
